import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Badge,
  useToast,
  SimpleGrid,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Alert,
  AlertIcon,
  Progress,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListIcon,
  Image,
} from "@chakra-ui/react";
import {
  FiCheckCircle,
  FiXCircle,
  FiDownload,
  FiEye,
  FiClock,
  FiUpload,
  FiUser,
  FiMail,
  FiCalendar,
  FiFileText,
  FiMessageSquare,
  FiSend,
} from "react-icons/fi";
import { useParams, useNavigate } from "react-router-dom";
import preBoardingService from "../services/preBoardingService";

const DocumentVerificationPage = () => {
  const { packetId } = useParams();
  const navigate = useNavigate();
  const [packet, setPacket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifyingIndex, setVerifyingIndex] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [verificationAction, setVerificationAction] = useState("verify");
  const [comments, setComments] = useState("");
  
  const toast = useToast();
  const { isOpen: isVerifyOpen, onOpen: onVerifyOpen, onClose: onVerifyClose } = useDisclosure();
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();

  useEffect(() => {
    if (packetId) {
      fetchPacket();
    }
  }, [packetId]);

  const fetchPacket = async () => {
    try {
      setLoading(true);
      const response = await preBoardingService.getPacketById(packetId);
      setPacket(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch pre-boarding packet",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyDocument = async () => {
    if (!selectedDocument) return;

    try {
      setVerifyingIndex(selectedDocument.index);
      await preBoardingService.verifyDocument(
        packetId,
        selectedDocument.index,
        verificationAction,
        comments
      );

      toast({
        title: "Success",
        description: `Document ${verificationAction}ed successfully`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Reset state
      setSelectedDocument(null);
      setComments("");
      onVerifyClose();
      fetchPacket(); // Refresh data
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to ${verificationAction} document`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setVerifyingIndex(null);
    }
  };

  const handleDownloadDocument = async (docIndex, fileName) => {
    try {
      const response = await preBoardingService.downloadDocument(packetId, docIndex);
      
      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || `document_${docIndex}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download document",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const openVerificationModal = (document, index, action) => {
    setSelectedDocument({ ...document, index });
    setVerificationAction(action);
    setComments("");
    onVerifyOpen();
  };

  const getStatusColor = (status) => {
    const statusColors = {
      pending: "yellow",
      uploaded: "blue",
      verified: "green",
      rejected: "red",
    };
    return statusColors[status] || "gray";
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: FiClock,
      uploaded: FiUpload,
      verified: FiCheckCircle,
      rejected: FiXCircle,
    };
    const IconComponent = icons[status] || FiClock;
    return <IconComponent />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const groupDocumentsByCategory = (documents) => {
    const grouped = {};
    documents.forEach((doc, index) => {
      const category = doc.documentType.category;
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push({ ...doc, originalIndex: index });
    });
    return grouped;
  };

  const isOverdue = () => {
    return packet && new Date(packet.dueDate) < new Date() && packet.status !== 'completed';
  };

  if (loading) {
    return (
      <Box p={6} textAlign="center">
        <Text>Loading packet details...</Text>
      </Box>
    );
  }

  if (!packet) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          Pre-boarding packet not found
        </Alert>
      </Box>
    );
  }

  const groupedDocuments = groupDocumentsByCategory(packet.documents);
  const uploadedDocs = packet.documents.filter(doc => doc.status === 'uploaded');
  const verifiedDocs = packet.documents.filter(doc => doc.status === 'verified');
  const rejectedDocs = packet.documents.filter(doc => doc.status === 'rejected');
  const mandatoryDocs = packet.documents.filter(doc => doc.documentType.isMandatory);

  return (
    <Box p={6} maxW="1400px" mx="auto">
      <VStack align="stretch" spacing={6}>
        {/* Header */}
        <HStack justify="space-between">
          <Box>
            <Heading size="lg">Document Verification</Heading>
            <Text color="gray.600">Review and verify candidate documents</Text>
          </Box>
          <Button onClick={() => navigate('/recruiting/preboarding')} variant="outline">
            Back to Dashboard
          </Button>
        </HStack>

        {/* Status Alerts */}
        {isOverdue() && (
          <Alert status="error">
            <AlertIcon />
            This pre-boarding packet is overdue! Due date was {formatDate(packet.dueDate)}
          </Alert>
        )}

        {uploadedDocs.length > 0 && (
          <Alert status="info">
            <AlertIcon />
            {uploadedDocs.length} document(s) are waiting for your review
          </Alert>
        )}

        {/* Progress Overview */}
        <SimpleGrid columns={{ base: 1, md: 5 }} spacing={4}>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Overall Progress</StatLabel>
                <StatNumber>{Math.round(packet.overallProgress || 0)}%</StatNumber>
                <Progress
                  value={packet.overallProgress || 0}
                  colorScheme="blue"
                  size="sm"
                  mt={2}
                />
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Verified</StatLabel>
                <StatNumber color="green.500">{verifiedDocs.length}</StatNumber>
                <StatHelpText>Documents approved</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Pending Review</StatLabel>
                <StatNumber color="blue.500">{uploadedDocs.length}</StatNumber>
                <StatHelpText>Need verification</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Rejected</StatLabel>
                <StatNumber color="red.500">{rejectedDocs.length}</StatNumber>
                <StatHelpText>Need re-upload</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Status</StatLabel>
                <StatNumber fontSize="sm">
                  <Badge colorScheme={getStatusColor(packet.status)}>
                    {packet.status.replace('_', ' ').toUpperCase()}
                  </Badge>
                </StatNumber>
                <StatHelpText>Current status</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Candidate Information */}
        <Card>
          <CardHeader>
            <Heading size="md">Candidate Information</Heading>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <VStack align="start" spacing={3}>
                <HStack>
                  <FiUser />
                  <Box>
                    <Text fontWeight="bold">Name</Text>
                    <Text>{packet.candidateName}</Text>
                  </Box>
                </HStack>
                <HStack>
                  <FiMail />
                  <Box>
                    <Text fontWeight="bold">Email</Text>
                    <Text>{packet.candidateEmail}</Text>
                  </Box>
                </HStack>
              </VStack>
              
              <VStack align="start" spacing={3}>
                <HStack>
                  <FiFileText />
                  <Box>
                    <Text fontWeight="bold">Position</Text>
                    <Text>{packet.metadata?.designation || 'N/A'}</Text>
                  </Box>
                </HStack>
                <HStack>
                  <FiCalendar />
                  <Box>
                    <Text fontWeight="bold">Expected Start</Text>
                    <Text>
                      {packet.metadata?.joiningDate 
                        ? formatDate(packet.metadata.joiningDate)
                        : 'Not specified'
                      }
                    </Text>
                  </Box>
                </HStack>
              </VStack>
            </SimpleGrid>
            
            {packet.metadata?.department && (
              <Box mt={4}>
                <Text fontWeight="bold">Department: </Text>
                <Text display="inline">{packet.metadata.department}</Text>
              </Box>
            )}
          </CardBody>
        </Card>

        {/* Documents by Category */}
        <Card>
          <CardHeader>
            <Heading size="md">Document Review</Heading>
            <Text fontSize="sm" color="gray.600">
              Review uploaded documents and verify or reject them
            </Text>
          </CardHeader>
          <CardBody>
            <Accordion allowMultiple>
              {Object.entries(groupedDocuments).map(([category, documents]) => {
                const categoryUploaded = documents.filter(doc => doc.status === 'uploaded').length;
                const categoryVerified = documents.filter(doc => doc.status === 'verified').length;
                
                return (
                  <AccordionItem key={category}>
                    <AccordionButton>
                      <Box flex="1" textAlign="left">
                        <HStack>
                          <Text fontWeight="medium">{category}</Text>
                          <Badge colorScheme="blue">
                            {categoryVerified} / {documents.filter(d => d.documentType.isMandatory).length} verified
                          </Badge>
                          {categoryUploaded > 0 && (
                            <Badge colorScheme="orange">
                              {categoryUploaded} pending
                            </Badge>
                          )}
                        </HStack>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                      <VStack spacing={4} align="stretch">
                        {documents.map((doc) => (
                          <Box
                            key={doc.originalIndex}
                            p={4}
                            border="1px"
                            borderColor="gray.200"
                            borderRadius="lg"
                            bg={doc.status === 'uploaded' ? 'orange.50' : 'inherit'}
                          >
                            <HStack justify="space-between" align="start">
                              <VStack align="start" spacing={2} flex={1}>
                                <HStack>
                                  <ListIcon 
                                    as={getStatusIcon(doc.status)} 
                                    color={`${getStatusColor(doc.status)}.500`} 
                                  />
                                  <Text fontWeight="medium">{doc.documentType.name}</Text>
                                  {doc.documentType.isMandatory && (
                                    <Badge size="xs" colorScheme="red">Required</Badge>
                                  )}
                                </HStack>
                                
                                {doc.documentType.description && (
                                  <Text fontSize="sm" color="gray.600">
                                    {doc.documentType.description}
                                  </Text>
                                )}

                                <HStack>
                                  <Badge colorScheme={getStatusColor(doc.status)}>
                                    {doc.status.toUpperCase()}
                                  </Badge>
                                  {doc.uploadedAt && (
                                    <Text fontSize="xs" color="gray.500">
                                      Uploaded: {formatDate(doc.uploadedAt)}
                                    </Text>
                                  )}
                                </HStack>

                                {doc.rejectionReason && (
                                  <Alert status="warning" size="sm">
                                    <AlertIcon />
                                    <Box>
                                      <Text fontWeight="bold" fontSize="sm">Rejection Reason:</Text>
                                      <Text fontSize="sm">{doc.rejectionReason}</Text>
                                    </Box>
                                  </Alert>
                                )}

                                {doc.verifiedAt && doc.verifiedBy && (
                                  <Text fontSize="xs" color="green.600">
                                    Verified by {doc.verifiedBy.name} on {formatDate(doc.verifiedAt)}
                                  </Text>
                                )}

                                {doc.comments && doc.comments.length > 0 && (
                                  <Box>
                                    <Text fontSize="sm" fontWeight="medium">Comments:</Text>
                                    {doc.comments.map((comment, idx) => (
                                      <Text key={idx} fontSize="sm" color="gray.600" ml={2}>
                                        • {comment.text}
                                      </Text>
                                    ))}
                                  </Box>
                                )}
                              </VStack>

                              {/* Action Buttons */}
                              <VStack spacing={2} minW="120px">
                                {doc.status === 'uploaded' && (
                                  <HStack spacing={2}>
                                    <Tooltip label="Approve Document">
                                      <IconButton
                                        icon={<FiCheckCircle />}
                                        size="sm"
                                        colorScheme="green"
                                        onClick={() => openVerificationModal(doc, doc.originalIndex, "verify")}
                                        isLoading={verifyingIndex === doc.originalIndex}
                                        aria-label="Approve"
                                      />
                                    </Tooltip>
                                    <Tooltip label="Reject Document">
                                      <IconButton
                                        icon={<FiXCircle />}
                                        size="sm"
                                        colorScheme="red"
                                        onClick={() => openVerificationModal(doc, doc.originalIndex, "reject")}
                                        isLoading={verifyingIndex === doc.originalIndex}
                                        aria-label="Reject"
                                      />
                                    </Tooltip>
                                  </HStack>
                                )}

                                {doc.filePath && (
                                  <HStack spacing={2}>
                                    <Tooltip label="Download Document">
                                      <IconButton
                                        icon={<FiDownload />}
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleDownloadDocument(doc.originalIndex, doc.originalFileName)}
                                        aria-label="Download"
                                      />
                                    </Tooltip>
                                    <Tooltip label="Preview Document">
                                      <IconButton
                                        icon={<FiEye />}
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          setSelectedDocument({ ...doc, index: doc.originalIndex });
                                          onPreviewOpen();
                                        }}
                                        aria-label="Preview"
                                      />
                                    </Tooltip>
                                  </HStack>
                                )}
                              </VStack>
                            </HStack>
                          </Box>
                        ))}
                      </VStack>
                    </AccordionPanel>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </CardBody>
        </Card>
      </VStack>

      {/* Verification Modal */}
      <Modal isOpen={isVerifyOpen} onClose={onVerifyClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {verificationAction === 'verify' ? 'Approve' : 'Reject'} Document
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box>
                <Text fontWeight="bold">Document:</Text>
                <Text>{selectedDocument?.documentType?.name}</Text>
              </Box>
              
              <Box>
                <Text fontWeight="bold" mb={2}>
                  {verificationAction === 'verify' ? 'Approval' : 'Rejection'} Comments:
                </Text>
                <Textarea
                  placeholder={
                    verificationAction === 'verify'
                      ? "Optional approval comments..."
                      : "Required: Explain why this document is being rejected"
                  }
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  rows={4}
                />
                {verificationAction === 'reject' && (
                  <Text fontSize="xs" color="red.500" mt={1}>
                    * Rejection reason is required to help candidate resubmit correctly
                  </Text>
                )}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onVerifyClose}>
              Cancel
            </Button>
            <Button
              colorScheme={verificationAction === 'verify' ? 'green' : 'red'}
              onClick={handleVerifyDocument}
              isDisabled={verificationAction === 'reject' && !comments.trim()}
              leftIcon={verificationAction === 'verify' ? <FiCheckCircle /> : <FiXCircle />}
            >
              {verificationAction === 'verify' ? 'Approve' : 'Reject'} Document
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Document Preview Modal */}
      <Modal isOpen={isPreviewOpen} onClose={onPreviewClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Document Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={4}>
              {selectedDocument?.documentType?.name}
            </Text>
            <Box textAlign="center" p={8} border="1px dashed" borderColor="gray.300" borderRadius="lg">
              <Text color="gray.500">
                Document preview would appear here in a production environment.
                For now, use the download button to view the file.
              </Text>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button
              leftIcon={<FiDownload />}
              onClick={() => handleDownloadDocument(selectedDocument?.index, selectedDocument?.originalFileName)}
              mr={3}
            >
              Download
            </Button>
            <Button variant="ghost" onClick={onPreviewClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default DocumentVerificationPage;